import React from "react";
import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import {
  TextField,
  FormHelperText,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  EmailOutlined,
  LockOutlined,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";

import { setUser } from "store/authSlice";
import { useLoginMutation } from "store/services/baseApi";

const INITIAL_STATE = {
  email: "",
  password: "",
};

const SignInForm = () => {
  const [showPassword, setShowPassword] = React.useState(false);
  const dispatch = useDispatch();
  const signInButtonRef = React.useRef();
  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: INITIAL_STATE,
  });
  const [loginUser, { isLoading: loggingIn }] = useLoginMutation();

  const handleEnterPress = (e) => {
    if (e.key === "Enter") {
      signInButtonRef?.current?.click();
    }
  };

  const handleSignIn = async (data) => {
    try {
      const userData = await loginUser(data).unwrap();
      dispatch(setUser(userData));
      reset(INITIAL_STATE);
    } catch (err) {
      toast.error(err?.data?.message ?? "Something went wrong!");
    }
  };

  return (
    <div className="flex w-full flex-col gap-4" onKeyDown={handleEnterPress}>
      <Controller
        name="email"
        control={control}
        rules={{ required: "Please provide your email." }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            placeholder="Enter email"
            size="small"
            type="email"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      {errors.email && (
        <FormHelperText error>{errors.email.message}</FormHelperText>
      )}

      <Controller
        name="password"
        control={control}
        rules={{ required: "Please enter your password." }}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            placeholder="Enter password"
            size="small"
            type={showPassword ? "text" : "password"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlined />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      {errors.password && (
        <FormHelperText error>{errors.password.message}</FormHelperText>
      )}

      <div className="flex flex-col gap-2 text-sm text-gray">
        <Link to="/forgot-password" className="hover:underline">
          Forgot Password?
        </Link>
      </div>

      <LoadingButton
        size="large"
        loading={loggingIn}
        loadingIndicator="Signing in..."
        variant="contained"
        ref={signInButtonRef}
        onClick={handleSubmit(handleSignIn)}
      >
        Sign In
      </LoadingButton>
    </div>
  );
};

export default SignInForm;